/**
 * Auto-captions — ANIMATED EMOJI ENTRY (Submagic-style pop / drop / spin / zoom).
 *
 * What this adds:
 *   Today emojis are pre-scaled to 80x80 and overlaid statically for their
 *   [start-0.1 … end+0.8] window. Submagic gives the emoji an "entry" pulse
 *   — it pops in, drops from above, spins on, or zooms from small. This
 *   module produces the FFmpeg `-filter_complex` overlay chain with a
 *   per-emoji entry animation when a non-`none` style is selected.
 *
 * Why a separate module:
 *   - Keeps auto-captions-pipeline.js out of the FFmpeg expression weeds.
 *   - Each animation needs its own split + per-event styled stream (scale
 *     with eval=frame, rotate with a-expression), which is harder to
 *     express inside the existing flat chain.
 *   - The dropdown UI mounts into the existing #ac-card-emoji card body
 *     via MutationObserver — no blade edit required for the card itself,
 *     just the label translations.
 *
 * Animation timings (driven by emoji event start S = ev.start):
 *   pop   : scale 0 → 1.2 over [S, S+0.2], then 1.2 → 1.0 over [S+0.2, S+0.4]
 *   drop  : y starts at target-100 px, drops to target over [S, S+0.5]
 *   spin  : rotation 0 → 2π over [S, S+0.5]
 *   zoom  : scale 0.4 → 1.0 over [S, S+0.4]
 *
 * FFmpeg notes:
 *   - `scale` with `eval=frame` re-evaluates w/h per frame; we wrap in
 *     `max(1,…)` so pop's leading 0× never produces a 0-width frame
 *     (some libavfilter builds refuse).
 *   - `rotate` defaults to a 0×0 background; we pin `c=none@0` (transparent)
 *     and `ow=iw:oh=ih` so the rotated emoji stays the same canvas size as
 *     the input (corners clip; emojis are roughly circular so OK).
 *   - `overlay` accepts `t` directly in its `x`/`y` expressions, so the
 *     drop animation just changes the y expression — no per-event scale.
 *   - Multiple events sharing one PNG input must `split` the upstream
 *     stream once per consumer (FFmpeg refuses to fork an unsplit input).
 *
 * Public API (window.__acAnimatedEmoji):
 *   getStyle()           → 'none' | 'pop' | 'drop' | 'spin' | 'zoom'
 *   setStyle(style)
 *   isEnabled()          → style !== 'none'
 *   buildOverlayChain(emojiEvents, baseInputs) → FFmpeg filter chain string
 *
 * baseInputs shape:
 *   {
 *     cpToIndex: { cp → ffmpeg input index (≥1) },
 *     yExpr:     'main_h*0.58' | …,   // target Y expression
 *     chainIn:   '[0:v]',             // initial chain label
 *     finalLabel:'[vemo]',            // last overlay output label
 *     size:       80                  // base emoji square px (optional)
 *   }
 *
 * Returns a filter-graph fragment ending in finalLabel. Callers append
 * `;<finalLabel>subtitles=…[vout]` afterwards. When the style is `none`
 * we fall through to the legacy chain (kept inside this module so the
 * pipeline has a single emoji-overlay code path).
 */
(function(){
    'use strict';

    var STORAGE_TOOL = 'auto-captions';
    var STORAGE_KEY  = 'animatedEmojiStyle';
    var VALID_STYLES = ['none', 'pop', 'drop', 'spin', 'zoom'];

    var state = {
        style: 'pop',  // default ON with the most Submagic-style pop
    };

    function clampStyle(s){
        return VALID_STYLES.indexOf(s) >= 0 ? s : 'none';
    }

    /* Read persisted style. We accept the legacy null/undefined case as
       "first visit" → fall back to the default ('pop') so the feature is
       discoverable, not silently off. */
    try {
        if (window.__toolSettings && typeof window.__toolSettings.load === 'function') {
            var saved = window.__toolSettings.load(STORAGE_TOOL, STORAGE_KEY, null);
            if (saved && VALID_STYLES.indexOf(saved) >= 0) {
                state.style = saved;
            }
        }
    } catch(e){}

    /* ───────────────────────── Expression builders ──────────────────── */

    /* Format a number as a short fixed-3 string. FFmpeg expressions are
       picky about locales — we always want '0.500', never '0,500'. */
    function f3(n){ return Number(n).toFixed(3); }

    /* Pop scale expression around start S:
         if(between(t, S, S+0.2),   (t-S)/0.2 * 1.2,
         if(between(t, S+0.2, S+0.4), 1.2 - (t-S-0.2)/0.2 * 0.2,
         1)) */
    function popScaleExpr(S){
        var s   = f3(S);
        var m1  = f3(S + 0.2);
        var m2  = f3(S + 0.4);
        return  'if(between(t,' + s + ',' + m1 + '),'
              +    '(t-' + s + ')/0.2*1.2,'
              + 'if(between(t,' + m1 + ',' + m2 + '),'
              +    '1.2-(t-' + m1 + ')/0.2*0.2,'
              +    '1))';
    }

    /* Zoom scale expression around start S:
         if(between(t, S, S+0.4), 0.4 + (t-S)/0.4 * 0.6, 1) */
    function zoomScaleExpr(S){
        var s  = f3(S);
        var e  = f3(S + 0.4);
        return 'if(between(t,' + s + ',' + e + '),'
             +   '0.4+(t-' + s + ')/0.4*0.6,'
             +   '1)';
    }

    /* Spin rotation (radians) around start S, going 0 → 2π over 0.5 s.
       rotate filter takes the `a` expression in radians. */
    function spinAngleExpr(S){
        var s  = f3(S);
        var e  = f3(S + 0.5);
        return 'if(between(t,' + s + ',' + e + '),'
             +   '(t-' + s + ')/0.5*2*PI,'
             +   '0)';
    }

    /* Drop y expression — y starts 100 px ABOVE target, eases linearly to
       target over [S, S+0.5]. After S+0.5 → target Y.
         if(between(t,S,S+0.5), Y - 100*(1-(t-S)/0.5), Y) */
    function dropYExpr(S, yExpr){
        var s = f3(S);
        var e = f3(S + 0.5);
        return 'if(between(t,' + s + ',' + e + '),'
             +   '(' + yExpr + ')-100*(1-(t-' + s + ')/0.5),'
             +   '(' + yExpr + '))';
    }

    /* ───────────────────────── Sanity helpers ───────────────────────── */

    /* Verify '(' and ')' balance — final guard before emitting a filter
       string into FFmpeg. Returns true if balanced. */
    function balancedParens(str){
        var depth = 0;
        for (var i = 0; i < str.length; i++){
            var c = str.charCodeAt(i);
            if (c === 40) depth++;
            else if (c === 41){
                depth--;
                if (depth < 0) return false;
            }
        }
        return depth === 0;
    }

    /* ───────────────────────── Chain builders ───────────────────────── */

    /* Legacy `none` path — pre-scale each unique input once, then chain
       overlays. Mirrors the pre-existing code in auto-captions-pipeline.js
       so flipping the style to 'none' produces identical FFmpeg arguments
       to the previous behaviour. */
    function buildStaticChain(events, baseInputs){
        var size      = baseInputs.size || 80;
        var yExpr     = baseInputs.yExpr;
        var chainIn   = baseInputs.chainIn || '[0:v]';
        var finalLab  = baseInputs.finalLabel || '[vemo]';
        var cpToIndex = baseInputs.cpToIndex || {};

        var uniq = {};
        events.forEach(function(ev){
            if (cpToIndex[ev.cp] != null) uniq[ev.cp] = true;
        });
        var cps = Object.keys(uniq);
        if (!cps.length) return '';

        var prelude = '';
        for (var k = 0; k < cps.length; k++){
            var ii = cpToIndex[cps[k]];
            prelude += '[' + ii + ':v]scale=' + size + ':' + size + ':flags=lanczos[e' + ii + '];';
        }

        var chunks = [];
        var run = chainIn;
        var validEvents = events.filter(function(ev){ return cpToIndex[ev.cp] != null; });
        validEvents.forEach(function(ev, idx){
            var last  = idx === validEvents.length - 1;
            var label = last ? finalLab : '[v' + (idx + 1) + ']';
            chunks.push(
                run + '[e' + cpToIndex[ev.cp] + ']' +
                'overlay=x=(main_w-overlay_w)/2:y=' + yExpr +
                ":enable='between(t," + f3(ev.start) + ',' + f3(ev.end) + ")'" + label
            );
            run = label;
        });
        return prelude + chunks.join(';');
    }

    /* Animated path — each event gets its OWN styled stream. Inputs are
       split N-ways per unique cp (N = number of events using that cp).
       Per-event filter:
         pop   → scale w/h time-varying, eval=frame
         zoom  → scale w/h time-varying, eval=frame
         spin  → fixed scale + rotate with time-varying angle
         drop  → fixed scale, animation moves into the overlay y expr
     */
    function buildAnimatedChain(events, baseInputs, style){
        var size      = baseInputs.size || 80;
        var yExpr     = baseInputs.yExpr;
        var chainIn   = baseInputs.chainIn || '[0:v]';
        var finalLab  = baseInputs.finalLabel || '[vemo]';
        var cpToIndex = baseInputs.cpToIndex || {};

        var validEvents = events.filter(function(ev){ return cpToIndex[ev.cp] != null; });
        if (!validEvents.length) return '';

        /* Count events per cp so we know how wide to split each input. */
        var perCpCount = {};
        validEvents.forEach(function(ev){
            perCpCount[ev.cp] = (perCpCount[ev.cp] || 0) + 1;
        });

        /* Split + assign per-event labels in source order. */
        var splitFragments = [];
        var cpCursor = {};               // cp → next split-index to consume
        var perEventSrcLabel = [];       // index matches validEvents order
        Object.keys(perCpCount).forEach(function(cp){
            var n  = perCpCount[cp];
            var ii = cpToIndex[cp];
            cpCursor[cp] = 0;
            if (n === 1){
                /* No split needed; one event reads directly from [ii:v] */
                splitFragments.push(null);  // placeholder; handled below
                return;
            }
            var outs = '';
            for (var s = 0; s < n; s++){
                outs += '[c' + ii + '_' + s + ']';
            }
            splitFragments.push('[' + ii + ':v]split=' + n + outs);
        });

        var splitPrelude = splitFragments.filter(function(f){ return f; }).join(';');

        /* Map each event → its source stream label. */
        validEvents.forEach(function(ev){
            var ii    = cpToIndex[ev.cp];
            var n     = perCpCount[ev.cp];
            var slot  = cpCursor[ev.cp]++;
            perEventSrcLabel.push(n === 1 ? '[' + ii + ':v]' : '[c' + ii + '_' + slot + ']');
        });

        /* Build per-event styled streams. */
        var styledFragments = [];
        validEvents.forEach(function(ev, idx){
            var src   = perEventSrcLabel[idx];
            var label = '[se' + idx + ']';
            var chain;

            if (style === 'pop' || style === 'zoom'){
                var scaleExpr = (style === 'pop')
                    ? popScaleExpr(ev.start)
                    : zoomScaleExpr(ev.start);
                /* max(1,…) avoids 0-width frames at the very start of pop. */
                var w = 'max(1,' + size + '*(' + scaleExpr + '))';
                chain = src + 'scale=w=\'' + w + "':h='" + w + "':eval=frame:flags=lanczos,format=rgba" + label;
            } else if (style === 'spin'){
                var ang = spinAngleExpr(ev.start);
                /* Pre-scale to fixed size, then rotate.  ow/oh kept at input
                   dimensions; transparent background via c=none. */
                chain = src
                    + 'scale=' + size + ':' + size + ':flags=lanczos,'
                    + "rotate=a='" + ang + "':ow=iw:oh=ih:c=none@0,format=rgba"
                    + label;
            } else {
                /* drop — static image, animation is in the overlay y */
                chain = src + 'scale=' + size + ':' + size + ':flags=lanczos,format=rgba' + label;
            }
            styledFragments.push(chain);
        });

        /* Compose the overlay chain. For drop, the y expression is per-event
           time-varying; for everything else it's the static target Y. */
        var chunks = [];
        var run = chainIn;
        validEvents.forEach(function(ev, idx){
            var last     = idx === validEvents.length - 1;
            var outLab   = last ? finalLab : '[v' + (idx + 1) + ']';
            var srcLab   = '[se' + idx + ']';
            var yPart    = (style === 'drop') ? dropYExpr(ev.start, yExpr) : yExpr;
            chunks.push(
                run + srcLab +
                'overlay=x=(main_w-overlay_w)/2:y=\'' + yPart + "'" +
                ":enable='between(t," + f3(ev.start) + ',' + f3(ev.end) + ")'" + outLab
            );
            run = outLab;
        });

        var parts = [];
        if (splitPrelude) parts.push(splitPrelude);
        parts.push(styledFragments.join(';'));
        parts.push(chunks.join(';'));
        return parts.filter(function(p){ return p && p.length; }).join(';');
    }

    /* Public chain builder. Returns '' if no usable events. */
    function buildOverlayChain(emojiEvents, baseInputs){
        var events = Array.isArray(emojiEvents) ? emojiEvents : [];
        if (!events.length) return '';
        if (!baseInputs || !baseInputs.cpToIndex || !baseInputs.yExpr) return '';

        var style = state.style;
        var out;
        if (style === 'none' || VALID_STYLES.indexOf(style) < 0){
            out = buildStaticChain(events, baseInputs);
        } else {
            out = buildAnimatedChain(events, baseInputs, style);
        }
        if (!out) return '';
        if (!balancedParens(out)){
            try { console.warn('[animated-emoji] unbalanced parens, falling back to static:', out); } catch(e){}
            out = buildStaticChain(events, baseInputs);
            if (!balancedParens(out)) return '';
        }
        return out;
    }

    /* ───────────────────────── UI mount ─────────────────────────────── */

    /* Read i18n labels from the emoji card body's data-attributes. The
       blade renders them so this module never imports translations. */
    function readLabels(host){
        function get(k, fb){
            var v = host.getAttribute('data-i18n-' + k);
            return (v == null || v === '') ? fb : v;
        }
        return {
            label: get('label',  'Animation'),
            none:  get('none',   'None'),
            pop:   get('pop',    'Pop'),
            drop:  get('drop',   'Drop'),
            spin:  get('spin',   'Spin'),
            zoom:  get('zoom',   'Zoom'),
        };
    }

    function mountDropdown(card){
        if (!card || card.querySelector('#ac-emoji-anim-select')) return;
        var body = card.querySelector('.ac-card-body');
        if (!body) return;

        var labels = readLabels(card);

        var wrap = document.createElement('div');
        wrap.className = 'ac-field';
        wrap.style.marginTop = '10px';

        var lbl = document.createElement('label');
        lbl.setAttribute('for', 'ac-emoji-anim-select');
        lbl.textContent = labels.label;
        lbl.style.display = 'block';
        lbl.style.fontSize = '12px';
        lbl.style.marginBottom = '4px';

        var sel = document.createElement('select');
        sel.id = 'ac-emoji-anim-select';
        sel.className = 'ac-input';

        VALID_STYLES.forEach(function(v){
            var opt = document.createElement('option');
            opt.value = v;
            opt.textContent = labels[v] || v;
            if (v === state.style) opt.selected = true;
            sel.appendChild(opt);
        });

        sel.addEventListener('change', function(){
            api.setStyle(sel.value);
            try {
                if (typeof window.__aeFeature === 'function'){
                    window.__aeFeature('animated_emoji_style', { style: state.style });
                }
            } catch(e){}
        });

        wrap.appendChild(lbl);
        wrap.appendChild(sel);
        body.appendChild(wrap);
    }

    /* The emoji card exists at page load, but in case the pipeline rebuilds
       the card body (rare — gate flip on first transcribe) we keep an
       observer for the first mount only. After that, we disconnect. */
    function tryMount(){
        var card = document.getElementById('ac-card-emoji');
        if (card){
            mountDropdown(card);
            return true;
        }
        return false;
    }

    function setupMount(){
        if (tryMount()) return;
        if (typeof MutationObserver !== 'function') return;
        var mo = new MutationObserver(function(){
            if (tryMount()){
                try { mo.disconnect(); } catch(e){}
            }
        });
        try {
            mo.observe(document.body || document.documentElement, { childList: true, subtree: true });
        } catch(e){}
    }

    if (typeof document !== 'undefined'){
        if (document.readyState === 'loading'){
            document.addEventListener('DOMContentLoaded', setupMount);
        } else {
            setupMount();
        }
    }

    /* ───────────────────────── Public API ───────────────────────────── */

    var api = {
        getStyle: function(){ return state.style; },
        setStyle: function(s){
            var v = clampStyle(s);
            state.style = v;
            try {
                if (window.__toolSettings && typeof window.__toolSettings.save === 'function'){
                    window.__toolSettings.save(STORAGE_TOOL, STORAGE_KEY, v);
                }
            } catch(e){}
            /* Reflect in the live UI if mounted by a non-UI caller. */
            try {
                var sel = document.getElementById('ac-emoji-anim-select');
                if (sel && sel.value !== v) sel.value = v;
            } catch(e){}
        },
        isEnabled: function(){ return state.style !== 'none'; },
        buildOverlayChain: buildOverlayChain,

        /* Internals exposed for testing — do NOT depend on these in production. */
        _popScaleExpr:   popScaleExpr,
        _zoomScaleExpr:  zoomScaleExpr,
        _spinAngleExpr:  spinAngleExpr,
        _dropYExpr:      dropYExpr,
        _balancedParens: balancedParens,
        _VALID_STYLES:   VALID_STYLES,
    };

    window.__acAnimatedEmoji = api;

    /* CommonJS export so the node test runner can `require()` it cleanly
       (vm.runInContext sees only window.__acAnimatedEmoji; the test uses
        that too). Mirrors video-filter-presets.js / cleaner.js pattern. */
    if (typeof module !== 'undefined' && module.exports){
        module.exports = api;
    }
})();
